import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/contexts/LanguageContext";
import { CheckCircle2, XCircle, Loader2, Clock, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { format } from "date-fns";
import slotifyLogoSrc from "@/assets/slotify-logo.svg";

const PaymentReturn = () => {
  const { shopId } = useParams();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const reservationId = searchParams.get("reservation_id");
  const orgId = searchParams.get("org_id");
  const stripeStatus = searchParams.get("status");

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<"pending" | "paid" | "failed" | "unknown">("pending");
  const [reservationNumber, setReservationNumber] = useState<string | null>(null);
  const [orgName, setOrgName] = useState("");
  const [serviceName, setServiceName] = useState("");
  const [reservationDate, setReservationDate] = useState<string | null>(null);
  const [reservationTime, setReservationTime] = useState<string | null>(null);
  const [reservationPrice, setReservationPrice] = useState<number | null>(null);
  const [selfServiceEnabled, setSelfServiceEnabled] = useState(false);

  useEffect(() => {
    if (!reservationId || !orgId) {
      setStatus("unknown");
      setLoading(false);
      return;
    }

    const checkStatus = async () => {
      // Fetch org name (via safe RPC)
      const { data: org } = await supabase
        .rpc("get_public_org_info", { _org_id: orgId })
        .single();
      if (org) setOrgName(org.name);

      // Check self-service extension (via safe RPC that strips secrets)
      const { data: ext } = await supabase
        .rpc("get_public_extension_info", { _organisation_id: orgId, _extension_id: "self-service" })
        .maybeSingle();
      if (ext?.enabled) {
        const settings = ext.settings as any;
        if (settings?.show_in_shop !== false) {
          setSelfServiceEnabled(true);
        }
      }

      // For Stripe, if status=cancelled from redirect, we know it failed
      if (stripeStatus === "cancelled") {
        setStatus("failed");
        setLoading(false);
        return;
      }

      // Poll reservation status
      // For Stripe payments, call verify-payment to check session status server-side
      let attempts = 0;
      const maxAttempts = 15;
      const poll = async () => {
        // First, try to verify the payment server-side (handles Stripe session check)
        if (stripeStatus === "success" && attempts < 3) {
          try {
            const { data: verifyResult } = await supabase.functions.invoke("verify-payment", {
              body: { reservation_id: reservationId, organisation_id: orgId },
            });
            if (verifyResult?.status === "pending" || verifyResult?.status === "confirmed") {
              // Payment verified — now fetch full reservation data
              const { data: reservation } = await supabase
                .from("reservations")
                .select("status, reservation_number, date, price, service_id")
                .eq("id", reservationId)
                .single();
              if (reservation) {
                setReservationNumber(reservation.reservation_number);
                setReservationPrice(reservation.price);
                if (reservation.date) {
                  try {
                    const d = new Date(reservation.date);
                    setReservationDate(format(d, "EEEE, MMM d, yyyy"));
                    setReservationTime(d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Amsterdam" }));
                  } catch { setReservationDate(reservation.date); }
                }
                if (reservation.service_id) {
                  const { data: svc } = await supabase.from("services").select("name").eq("id", reservation.service_id).single();
                  if (svc) setServiceName(svc.name);
                }
              }
              setStatus("paid");
              setLoading(false);
              return;
            }
            if (verifyResult?.status === "cancelled") {
              setStatus("failed");
              setLoading(false);
              return;
            }
          } catch (e) {
            console.error("verify-payment error:", e);
          }
        }

        // Fallback: poll reservation status directly (works for Mollie webhook flow)
        const { data: reservation } = await supabase
          .from("reservations")
          .select("status, reservation_number, date, price, service_id")
          .eq("id", reservationId)
          .single();

        if (!reservation) {
          setStatus("unknown");
          setLoading(false);
          return;
        }

        setReservationNumber(reservation.reservation_number);
        setReservationPrice(reservation.price);

        // Parse date and time
        if (reservation.date) {
          try {
            const d = new Date(reservation.date);
            setReservationDate(format(d, "EEEE, MMM d, yyyy"));
            setReservationTime(d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Amsterdam" }));
          } catch {
            setReservationDate(reservation.date);
          }
        }

        // Fetch service name
        if (reservation.service_id) {
          const { data: svc } = await supabase
            .from("services")
            .select("name")
            .eq("id", reservation.service_id)
            .single();
          if (svc) setServiceName(svc.name);
        }

        if (reservation.status === "pending" || reservation.status === "confirmed") {
          setStatus("paid");
          setLoading(false);
          return;
        }

        if (reservation.status === "cancelled" || reservation.status === "expired") {
          setStatus("failed");
          setLoading(false);
          return;
        }

        attempts++;
        if (attempts >= maxAttempts) {
          setStatus("pending");
          setLoading(false);
          return;
        }

        setTimeout(poll, 2000);
      };

      poll();
    };

    checkStatus();
  }, [reservationId, orgId, stripeStatus]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto" />
          <p className="text-muted-foreground text-sm">{t("shop.payment_checking")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background p-6">
      <div className="max-w-md w-full">
        {status === "paid" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="text-center py-8"
          >
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-accent/10">
              <CheckCircle2 className="h-8 w-8 text-accent" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">{t("shop.payment_success_title")}</h2>
            {reservationNumber && (
              <p className="mt-1 font-mono text-sm text-muted-foreground">{reservationNumber}</p>
            )}
            <p className="mt-2 text-muted-foreground">{t("shop.payment_success_desc")}</p>

            {/* Booking summary card */}
            {(serviceName || reservationDate) && (
              <div className="mx-auto mt-6 max-w-sm rounded-xl bg-muted/50 p-4 text-left">
                <div className="space-y-2 text-sm">
                  {serviceName && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("shop.service")}</span>
                      <span className="font-medium text-foreground">{serviceName}</span>
                    </div>
                  )}
                  {reservationDate && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("shop.date_time")}</span>
                      <span className="font-medium text-foreground">{reservationDate}</span>
                    </div>
                  )}
                  {reservationTime && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Time</span>
                      <span className="font-medium text-foreground">{reservationTime}</span>
                    </div>
                  )}
                  {reservationPrice != null && (
                    <div className="flex justify-between border-t border-border pt-2">
                      <span className="font-medium text-foreground">{t("shop.total")}</span>
                      <span className="font-bold text-primary">€{reservationPrice.toFixed(2)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <p className="mt-6 text-xs text-muted-foreground">{t("shop.reminder")}</p>

            {shopId && (
              <Button className="mt-6" asChild>
                <Link to={`/shop/${shopId}`}>
                  {t("shop.book_another") || "Book another appointment"}
                </Link>
              </Button>
            )}

            {selfServiceEnabled && shopId && (
              <div className="mt-3">
                <Link
                  to={`/shop/${shopId}/manage`}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Settings2 className="h-3.5 w-3.5" />
                  {t("ext.ss_manage_link")}
                </Link>
              </div>
            )}
          </motion.div>
        )}

        {status === "failed" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="text-center py-8"
          >
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <XCircle className="h-8 w-8 text-destructive" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">{t("shop.payment_failed_title")}</h2>
            <p className="mt-2 text-muted-foreground">{t("shop.payment_failed_desc")}</p>
            {shopId && (
              <Button className="mt-6" asChild>
                <Link to={`/shop/${shopId}`}>{t("shop.payment_try_again")}</Link>
              </Button>
            )}
          </motion.div>
        )}

        {status === "pending" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="text-center py-8"
          >
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-500/10">
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">{t("shop.payment_pending_title")}</h2>
            <p className="mt-2 text-muted-foreground">{t("shop.payment_pending_desc")}</p>
          </motion.div>
        )}

        {status === "unknown" && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className="text-center py-8"
          >
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <XCircle className="h-8 w-8 text-muted-foreground" />
            </div>
            <h2 className="text-2xl font-bold text-foreground">{t("shop.payment_unknown_title")}</h2>
            <p className="mt-2 text-muted-foreground">{t("shop.payment_unknown_desc")}</p>
          </motion.div>
        )}

        {orgName && (
          <p className="text-xs text-muted-foreground text-center pt-4">{orgName}</p>
        )}

        {/* Powered by Slotify footer */}
        <div className="mt-8 text-center">
          <a
            href="https://slotify.nl"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground/60 hover:text-muted-foreground transition-colors"
          >
            Powered by
            <img src={slotifyLogoSrc} alt="Slotify" className="h-3.5" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default PaymentReturn;
